<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Refund Payment</h1>
      <p class="subtitle">Enter the amount to refund. The maximum refund amount is the net payment amount (payment minus any previous refunds).</p>
    </div>
    <div class="modal-card-body">
      <div class="form-grid">
        <div class="amount-info">
          <div class="amount-info-item">
            <span class="amount-label">Maximum Refund</span>
            <span class="amount-value">{{ formatUtils.formatCurrency(maximumRefundAmount) }}</span>
          </div>
        </div>

        <div class="form-row">
          <label for="refundAmount" class="form-label">Refund Amount</label>
          <MonetaryInput v-model="refundAmountCents" />
        </div>

        <div class="form-row">
          <label class="form-label">Refund Date</label>
          <DateInput v-model="dateRefunded" />
        </div>

        <div class="form-row">
          <label class="form-label">Reason (optional)</label>
          <TextareaInput v-model="reasonRefunded" placeholder="Enter reason for refund..." :min-rows="2" />
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click" @click="handleCancel">
        Cancel
      </button>
      <button class="button primary form-button" @click="handleSubmit" :disabled="isSubmitting">
        {{ isSubmitting ? 'Processing...' : 'Process Refund' }}
      </button>
    </div>
    <div v-if="error" class="modal-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import MonetaryInput from '@/components/inputs/MonetaryInput.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as formatUtils from '@/utils/formatters';
import * as api from '@/api';
import { getLocalToday } from '@/utils/dateUtils';

export default {
  name: "RefundPaymentModal",
  props: {
    payment: Object
  },
  emits: ['close', 'refunded'],
  data() {
    return {
      formatUtils,
      dateRefunded: getLocalToday(),
      refundAmountCents: 0,
      reasonRefunded: '',
      isSubmitting: false,
      error: null
    }
  },
  computed: {
    maximumRefundAmount() {
      return this.getNetAmount();
    }
  },
  methods: {
    handleCancel() {
      // Close modal via close-on-click class
    },
    getNetAmount() {
      return this.payment.amountCentsPaid - this.payment.amountCentsRefunded;
    },
    async handleSubmit() {
      this.error = null;

      if (!this.refundAmountCents || this.refundAmountCents <= 0) {
        this.error = 'Please enter a valid refund amount';
        return;
      }

      const netAmount = this.getNetAmount();
      if (this.refundAmountCents > netAmount) {
        this.error = 'Refund amount cannot exceed the maximum refund amount (' + formatUtils.formatCurrency(netAmount) + ')';
        return;
      }

      this.isSubmitting = true;

      try {
        const refundData = {
          amountCentsToRefund: this.refundAmountCents,
          dateRefunded: this.dateRefunded,
          reasonRefunded: this.reasonRefunded.trim() || null
        };

        await api.refundPayment(this.payment.paymentId, refundData);

        this.$emit('refunded');

        const closeButton = this.$el.querySelector('.close-on-click');
        if (closeButton) {
          closeButton.click();
        }
      } catch (err) {
        this.error = err.message || 'Failed to process refund';
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  components: { CloseIcon, MonetaryInput, DateInput, TextareaInput }
};
</script>

<style scoped>
.modal-card {
  max-width: 480px;
}

.subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  font-weight: 400;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.amount-info {
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: var(--border-radius);
  border: 1px solid #e5e7eb;
}

.amount-info-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.amount-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-value {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-card-footer {
  display: flex;
  gap: 16px;
}
</style>
